import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";
import moment from "moment/moment";

const extractFileName = (url) => {
  if (!url) return "";
  const parts = url.split("/");
  const fileNameWithUUID = parts[parts.length - 1];
  const fileNameParts = fileNameWithUUID.split("-");
  return fileNameParts[fileNameParts.length - 1];
};

const getActionChipColor = (action) => {
  const a = action?.toLowerCase();
  if (a === "uploaded") return "success";
  if (a === "deleted") return "error";
  if (a === "edited" || a === "updated") return "warning";
  return "primary";
};

const buildPerDocumentHistory = (documents, filterId) => {
  const doc = documents?.find((d) => d._id === filterId);
  if (!doc) return [];
  const history = Array.isArray(doc.versionHistory) ? doc.versionHistory : [];
  return [...history]
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
    .map((entry, index) => ({
      key: entry._id || `${doc._id}-v-${index}`,
      documentId: doc._id,
      title: doc.title,
      version: entry.version || "1.0",
      action: entry.action || "Updated",
      actor: entry.actorName || "Unknown",
      timestamp: entry.timestamp,
      fileUrl: entry.fileUrl,
      fileType: entry.fileType,
      fileName: entry.fileName || extractFileName(entry.fileUrl || ""),
      notes: entry.notes || "",
      current: index === 0,
    }));
};

const buildCaseHistory = (documents) => {
  if (!Array.isArray(documents)) return [];
  return documents
    .flatMap((doc) => {
      const history = Array.isArray(doc.versionHistory) ? doc.versionHistory : [];
      return history.map((entry, index) => ({
        key: entry._id || `${doc._id}-v-${index}`,
        documentId: doc._id,
        title: doc.title,
        version: entry.version || "1.0",
        action: entry.action || "Updated",
        actor: entry.actorName || "Unknown",
        timestamp: entry.timestamp,
        fileUrl: entry.fileUrl,
        fileType: entry.fileType,
        fileName: entry.fileName || extractFileName(entry.fileUrl || ""),
        notes: entry.notes || "",
        current: index === 0,
      }));
    })
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
};

const DocumentHistoryDialog = ({
  open,
  onClose,
  documents,
  initialFilter,
  onView,
  onDownload,
  onShare,
  onBookmark,
}) => {
  const [historyFilter, setHistoryFilter] = useState(initialFilter || null);

  useEffect(() => {
    if (open) {
      setHistoryFilter(initialFilter || null);
    }
  }, [open, initialFilter]);

  const filteredHistoryEntries = historyFilter
    ? buildPerDocumentHistory(documents, historyFilter)
    : buildCaseHistory(documents);

  const handleClose = () => {
    setHistoryFilter(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {historyFilter
          ? `Document History — ${documents?.find((d) => d._id === historyFilter)?.title || "Document"}`
          : "Case Document History"}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {historyFilter && (
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon sx={{ fontSize: "14px" }} />}
              onClick={() => setHistoryFilter(null)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#003D78",
                whiteSpace: "nowrap",
              }}
            >
              View full case history
            </Button>
          )}
        </Box>

        {filteredHistoryEntries.length > 0 ? (
          <Stack spacing={1.5}>
            {filteredHistoryEntries.map((entry) => (
              <Box
                key={entry.key}
                sx={{
                  border: "1px solid #e8e8e8",
                  borderRadius: "12px",
                  p: 1.5,
                  bgcolor: entry.current ? "#f8fbff" : "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101936" }}>
                      {entry.title}
                    </Typography>
                    {entry.notes ? (
                      <Typography sx={{ fontSize: "12px", color: "#444" }}>
                        Description: {entry.notes}
                      </Typography>
                    ) : null}
                    <Typography sx={{ fontSize: "12px", color: "#666" }}>
                      By: {entry.actor}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666" }}>
                      {moment(entry.timestamp).format("D MMM YYYY, h:mm A")}
                    </Typography>
                  </Box>

                  <Stack direction="column" spacing={0.5}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="small" label={entry.version} sx={{ fontSize: "11px" }} />
                      <Chip
                        size="small"
                        label={entry.action}
                        color={getActionChipColor(entry.action)}
                        sx={{ fontSize: "11px" }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onView(entry.fileUrl, entry.fileType || "application/pdf")}
                        disabled={!entry.fileUrl}
                        sx={{ textTransform: "none" }}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onDownload(entry.fileUrl)}
                        disabled={!entry.fileUrl}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#003D78",
                          "&:hover": { backgroundColor: "#002b57" },
                        }}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onShare(entry)}
                        disabled={!entry.fileUrl}
                        sx={{ textTransform: "none" }}
                      >
                        Share
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onBookmark(entry)}
                        disabled={!entry.fileUrl}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#003D78",
                          "&:hover": { backgroundColor: "#002b57" },
                        }}
                      >
                        Bookmark
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box sx={{ py: 4, textAlign: "center", color: "#666", fontSize: "13px" }}>
            {historyFilter
              ? "No history found for this document."
              : "No document history found for this case."}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentHistoryDialog;
